import { motion } from "framer-motion";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "@/components/ui/accordion";
import {
    HelpCircle,
    MessageCircle,
    Phone,
    Mail,
    FileText,
    CreditCard,
    Wallet,
    Shield,
    Clock,
    Send,
    ExternalLink,
    Activity,
} from "lucide-react";
import { pageTransition, staggerContainer, staggerItem } from "@/lib/animations";
import { useState, useMemo } from "react";
import { useToast } from "@/hooks/use-toast";
import { useOrgSettings } from "@/lib/org-settings-context";

const buildFaqs = (currencySymbol: string) => [
    {
        category: "Getting Started",
        icon: HelpCircle,
        questions: [
            {
                q: "How do I create an account?",
                a: "You can create an account by providing your phone number. We'll send you an OTP to verify your number, and you'll be able to access your portal immediately.",
            },
            {
                q: "What documents do I need for KYC verification?",
                a: "For KYC verification, you'll need your Aadhaar card and PAN card. The verification process is quick and can be completed entirely online through our secure portal.",
            },
            {
                q: "How long does KYC verification take?",
                a: "KYC verification typically takes 24-48 hours. You'll receive a notification once your verification is complete. You can use basic features while verification is pending.",
            },
        ],
    },
    {
        category: "Virtual Cards",
        icon: CreditCard,
        questions: [
            {
                q: "How do I request a virtual card?",
                a: "Navigate to the 'My Cards' section and click 'Request Card'. Choose your card type, set spending limits, and your virtual card will be issued instantly.",
            },
            {
                q: "Can I set spending limits on my card?",
                a: "Yes! You can set daily and monthly spending limits when requesting a card. You can also modify these limits later from the card details page.",
            },
            {
                q: "How do I freeze/unfreeze my card?",
                a: "Go to 'My Cards', select the card you want to manage, and click the 'Freeze' or 'Unfreeze' button. This takes effect immediately.",
            },
            {
                q: "How do virtual cards work?",
                a: "Virtual cards are issued instantly and can be used for online and in-store contactless transactions. You can manage spending limits and freeze/unfreeze cards at any time.",
            },
        ],
    },
    {
        category: "Wallet & Payments",
        icon: Wallet,
        questions: [
            {
                q: "How do I add money to my wallet?",
                a: "Click on 'Add Money' in your wallet section. Enter the amount and choose your payment method (UPI, Net Banking, or Debit Card). Funds are added instantly.",
            },
            {
                q: "What are the wallet transaction limits?",
                a: `Minimum top-up: ${currencySymbol}10. Maximum per transaction: ${currencySymbol}1,00,000. Daily limit: ${currencySymbol}2,00,000. Monthly limit: ${currencySymbol}10,00,000 (subject to KYC tier).`,
            },
            {
                q: "How long do refunds take?",
                a: "Refunds are processed within 5-7 business days and will be credited back to your wallet or original payment method.",
            },
        ],
    },
    {
        category: "Security",
        icon: Shield,
        questions: [
            {
                q: "How is my data protected?",
                a: "We use bank-grade 256-bit SSL encryption for all data transmission. Your sensitive information is encrypted at rest and we never store your full card details.",
            },
            {
                q: "What should I do if I suspect fraud?",
                a: "Immediately freeze your card from the app, contact our support team at support@payprism.io, and we'll investigate the issue within 24 hours.",
            },
            {
                q: "Can I enable two-factor authentication?",
                a: "Yes! Go to Account Settings > Security and enable 2FA. We recommend using an authenticator app for maximum security.",
            },
        ],
    },
];

const contactMethods = [
    {
        icon: Phone,
        title: "Phone Support",
        description: "Coming Soon",
        value: "Coming Soon",
        action: "Coming Soon",
        href: "#",
        comingSoon: true,
    },
    {
        icon: Mail,
        title: "Email Support",
        description: "Response within 24 hours",
        value: "support@payprism.io",
        action: "Send Email",
        href: "mailto:support@payprism.io",
    },
    {
        icon: MessageCircle,
        title: "Live Chat",
        description: "Coming Soon",
        value: "Coming Soon",
        action: "Coming Soon",
        href: "#",
        comingSoon: true,
    },
    {
        icon: Activity,
        title: "System Status",
        description: "Real-time service health",
        value: "All systems monitored 24/7",
        action: "View Status",
        href: "https://vedalogic.betteruptime.com/",
    },
];

const resources = [
    {
        icon: FileText,
        title: "User Guide",
        description: "Complete guide to using the platform",
        href: "#",
    },
    {
        icon: Shield,
        title: "Security Best Practices",
        description: "Keep your account safe and secure",
        href: "#",
    },
    {
        icon: CreditCard,
        title: "Card Management Guide",
        description: "Learn how to manage your cards",
        href: "#",
    },
];

export default function PortalHelp() {
    const { toast } = useToast();
    const { currencySymbol } = useOrgSettings();
    const faqs = useMemo(() => buildFaqs(currencySymbol), [currencySymbol]);
    const [formData, setFormData] = useState({
        subject: "",
        message: "",
    });
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSubmitting(true);

        // Simulate API call
        await new Promise((resolve) => setTimeout(resolve, 1500));

        toast({
            title: "Message Sent!",
            description: "We'll get back to you within 24 hours.",
        });

        setFormData({ subject: "", message: "" });
        setIsSubmitting(false);
    };

    return (
        <motion.div
            variants={pageTransition}
            initial="initial"
            animate="animate"
            exit="exit"
            className="p-4 md:p-6 space-y-6 md:space-y-8 max-w-7xl mx-auto"
        >
            {/* Header */}
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-center"
            >
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 mb-4">
                    <HelpCircle className="h-8 w-8 text-primary" />
                </div>
                <h1 className="text-3xl md:text-4xl font-bold">Help & Support</h1>
                <p className="text-muted-foreground mt-2 max-w-2xl mx-auto">
                    Find answers to common questions or get in touch with our support team
                </p>
            </motion.div>

            {/* Contact Methods */}
            <motion.div
                variants={staggerContainer}
                initial="initial"
                animate="animate"
                className="grid gap-4 md:grid-cols-2 lg:grid-cols-4"
            >
                {contactMethods.map((method, index) => (
                    <motion.div key={index} variants={staggerItem}>
                        <Card className={`h-full transition-all ${method.comingSoon ? "opacity-60" : "hover:border-primary/50"}`}>
                            <CardContent className="p-6">
                                <div className="flex flex-col items-center text-center space-y-3">
                                    <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
                                        <method.icon className="h-6 w-6 text-primary" />
                                    </div>
                                    <div>
                                        <h3 className="font-semibold flex items-center gap-2 justify-center">
                                            {method.title}
                                            {method.comingSoon && (
                                                <Badge variant="outline" className="text-[10px] px-1.5 py-0 bg-amber-100 text-amber-700 border-amber-300">Coming Soon</Badge>
                                            )}
                                        </h3>
                                        <p className="text-xs text-muted-foreground mt-1">
                                            {method.description}
                                        </p>
                                    </div>
                                    <p className="text-sm font-medium">{method.value}</p>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        className="w-full"
                                        disabled={method.comingSoon}
                                        onClick={() => {
                                            if (method.href === "#") {
                                                toast({
                                                    title: "Coming Soon",
                                                    description: "This feature will be available soon!",
                                                });
                                            } else if (method.href.startsWith("http")) {
                                                window.open(method.href, "_blank", "noopener,noreferrer");
                                            } else {
                                                window.location.href = method.href;
                                            }
                                        }}
                                    >
                                        {method.action}
                                        {method.href.startsWith("http") && <ExternalLink className="h-3 w-3 ml-1.5" />}
                                    </Button>
                                </div>
                            </CardContent>
                        </Card>
                    </motion.div>
                ))}
            </motion.div>

            {/* FAQs */}
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
            >
                <Card>
                    <CardHeader>
                        <CardTitle>Frequently Asked Questions</CardTitle>
                        <CardDescription>
                            Quick answers to common questions
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <div className="space-y-6">
                            {faqs.map((category, categoryIndex) => (
                                <div key={categoryIndex}>
                                    <div className="flex items-center gap-2 mb-3">
                                        <category.icon className="h-5 w-5 text-primary" />
                                        <h3 className="font-semibold text-lg">{category.category}</h3>
                                    </div>
                                    <Accordion type="single" collapsible className="w-full">
                                        {category.questions.map((faq, faqIndex) => (
                                            <AccordionItem
                                                key={faqIndex}
                                                value={`${categoryIndex}-${faqIndex}`}
                                            >
                                                <AccordionTrigger className="text-left">
                                                    {faq.q}
                                                </AccordionTrigger>
                                                <AccordionContent className="text-muted-foreground">
                                                    {faq.a}
                                                </AccordionContent>
                                            </AccordionItem>
                                        ))}
                                    </Accordion>
                                </div>
                            ))}
                        </div>
                    </CardContent>
                </Card>
            </motion.div>

            {/* Contact Form */}
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
            >
                <Card>
                    <CardHeader>
                        <CardTitle>Send Us a Message</CardTitle>
                        <CardDescription>
                            Can't find what you're looking for? Send us a message and we'll get back to you.
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div className="space-y-2">
                                <Label htmlFor="subject">Subject</Label>
                                <Input
                                    id="subject"
                                    placeholder="What do you need help with?"
                                    value={formData.subject}
                                    onChange={(e) =>
                                        setFormData({ ...formData, subject: e.target.value })
                                    }
                                    required
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="message">Message</Label>
                                <Textarea
                                    id="message"
                                    placeholder="Describe your issue or question in detail..."
                                    rows={6}
                                    value={formData.message}
                                    onChange={(e) =>
                                        setFormData({ ...formData, message: e.target.value })
                                    }
                                    required
                                />
                            </div>
                            <Button type="submit" disabled={isSubmitting} className="w-full md:w-auto">
                                {isSubmitting ? (
                                    <>
                                        <Clock className="h-4 w-4 mr-2 animate-spin" />
                                        Sending...
                                    </>
                                ) : (
                                    <>
                                        <Send className="h-4 w-4 mr-2" />
                                        Send Message
                                    </>
                                )}
                            </Button>
                        </form>
                    </CardContent>
                </Card>
            </motion.div>

            {/* Resources */}
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
            >
                <Card>
                    <CardHeader>
                        <CardTitle>Helpful Resources</CardTitle>
                        <CardDescription>
                            Guides and documentation to help you get the most out of our platform
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <div className="grid gap-4 md:grid-cols-3">
                            {resources.map((resource, index) => (
                                <button
                                    key={index}
                                    onClick={() => {
                                        toast({
                                            title: "Coming Soon",
                                            description: "This resource will be available soon!",
                                        });
                                    }}
                                    className="flex items-start gap-3 p-4 rounded-lg border hover:border-primary/50 hover:bg-accent/50 transition-all text-left"
                                >
                                    <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                                        <resource.icon className="h-5 w-5 text-primary" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2">
                                            <h4 className="font-medium">{resource.title}</h4>
                                            <ExternalLink className="h-3 w-3 text-muted-foreground" />
                                        </div>
                                        <p className="text-sm text-muted-foreground mt-1">
                                            {resource.description}
                                        </p>
                                    </div>
                                </button>
                            ))}
                        </div>
                    </CardContent>
                </Card>
            </motion.div>

            {/* Business Hours */}
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
            >
                <Card className="bg-gradient-to-br from-primary/5 to-primary/10 border-primary/20">
                    <CardContent className="p-6">
                        <div className="flex items-start gap-4">
                            <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                                <Clock className="h-6 w-6 text-primary" />
                            </div>
                            <div className="flex-1">
                                <h3 className="font-semibold text-lg mb-2">Support Hours</h3>
                                <div className="grid gap-2 text-sm">
                                    <div className="flex justify-between">
                                        <span className="text-muted-foreground">Monday - Friday:</span>
                                        <span className="font-medium">9:00 AM - 6:00 PM IST</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-muted-foreground">Saturday:</span>
                                        <span className="font-medium">10:00 AM - 4:00 PM IST</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-muted-foreground">Sunday:</span>
                                        <span className="font-medium">Closed</span>
                                    </div>
                                    <div className="mt-2 pt-2 border-t border-primary/20">
                                        <p className="text-xs text-muted-foreground">
                                            Emergency support available 24/7 for critical issues
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </motion.div>
        </motion.div>
    );
}
